import React, { useState, useEffect, ChangeEvent } from 'react'
import { InputGroup, Menu, MenuItem, Classes } from '@blueprintjs/core'
import { Icon, Button, Flex, HighlightedText, ColorSquare } from '@ui'
import { navigate } from '@reach/router'
import { pluralize } from '@utils'
import useNeighborhoodsSearch from '../hooks/useNeighborhoodsSearch'
import css from './neighborhoodCatalog.module.scss'

interface NeighborhoodCatalogProps {
  floorId?: string
}

export default function NeighborhoodCatalog({ floorId }: NeighborhoodCatalogProps) {
  const [empty, setEmpty] = useState(false)

  const neighborhoods = useNeighborhoodsSearch(floorId)

  useEffect(() => {
    if (!neighborhoods.loading && !neighborhoods.count && !neighborhoods.query && !empty) {
      setEmpty(true)
    }

    if (!neighborhoods.loading && neighborhoods.count && !neighborhoods.query && empty) {
      setEmpty(false)
    }
  }, [neighborhoods.count, empty, neighborhoods.loading, neighborhoods.query])

  if (empty) return <Empty floorId={floorId} />

  return (
    <div className={css.neighborhood_catalog_container}>
      <div className={css.input_container}>
        <InputGroup
          fill
          value={neighborhoods.query}
          onChange={(e: ChangeEvent<HTMLInputElement>) => neighborhoods.run(e.target.value)}
          data-input="manage_neighborhood_search"
          placeholder="Search..."
          leftIcon={<Icon name="search" />}
        />
      </div>
      <div>
        {neighborhoods.loading && <Skeleton countLines={1} withSquare={false} />}
        {!neighborhoods.loading && (
          <div className={css.results_title}>
            {neighborhoods.items.length} {pluralize(neighborhoods.count, 'record')}
          </div>
        )}
        <Menu>
          {neighborhoods.loading && <Skeleton countLines={3} />}
          {!neighborhoods.loading && (
            <>
              <MenuItem
                key="add_item"
                onClick={() => floorId && navigate(`?newItem=neighborhood&floorId=${floorId}`)}
                text={
                  <Flex
                    align="center"
                    data-button="new_neighborhood"
                    style={{ color: 'var(--primary-color)', width: '100%' }}
                  >
                    <Icon name="plus" style={{ paddingRight: 8 }} />
                    New Neighborhood
                  </Flex>
                }
              />
              {neighborhoods.items.map((nh) => (
                <MenuItem
                  key={nh.id}
                  onClick={() => navigate(`?neighborhoodId=${nh.id}`)}
                  text={
                    <NeighborhoodItem
                      name={nh.name}
                      color={nh.color}
                      search={neighborhoods.query}
                      employeesCount={nh.counters.employees}
                      spacesCount={nh.counters.workstations}
                    />
                  }
                />
              ))}
            </>
          )}
        </Menu>
      </div>
    </div>
  )
}

function Empty({ floorId }: { floorId?: string }) {
  return (
    <div className={css.container}>
      <div className={css.icon_wrapper}>
        <Icon
          duotone
          name="construction"
          style={{ fontSize: 34, color: 'var(--smashing-pumpkins)' }}
        />
      </div>
      <div className={css.label}>
        <strong>No neighborhoods</strong>
        <Button
          data-button="new_neighborhood"
          iconName="plus"
          minimal
          blue
          text="New Neighborhood"
          style={{ display: 'block' }}
          fill={false}
          disabled={!floorId}
          onClick={() => {
            if (floorId) navigate(`?newItem=neighborhood&floorId=${floorId}`)
          }}
        />
      </div>
    </div>
  )
}

export function NeighborhoodItem({
  name,
  color,
  search,
  employeesCount,
  spacesCount,
}: {
  name: string
  color: string
  search?: string
  employeesCount?: number
  spacesCount?: number
}) {
  return (
    <Flex data-item={name} align="center">
      <ColorSquare style={{ background: `#${color}`, marginRight: 14 }} />
      <div>
        <div
          className={css.neighborhood_name}
          style={{ lineHeight: '14px', fontSize: 14, color: 'var(--black)' }}
        >
          <HighlightedText text={name} query={search} />
          {employeesCount && spacesCount && (
            <span style={{ fontSize: 10, color: 'var(--grey1)' }}>
              {`${employeesCount} ${pluralize(
                employeesCount,
                'employee'
              )} | ${spacesCount} ${pluralize(spacesCount, 'space')}`}
            </span>
          )}
        </div>
      </div>
    </Flex>
  )
}

function Skeleton({
  countLines = 6,
  withSquare = true,
}: {
  countLines?: number
  withSquare?: boolean
}) {
  function Placeholder() {
    return (
      <Flex align="center" style={{ padding: '0 7px', height: 40, width: 280 }}>
        {withSquare && (
          <div
            style={{
              width: 14,
              height: 14,
              marginRight: 10,
            }}
            className={Classes.SKELETON}
          >
            Placeholder text
          </div>
        )}
        <div
          style={{
            width: 150,
            height: 16,
          }}
          className={Classes.SKELETON}
        >
          Placeholder text
        </div>
      </Flex>
    )
  }

  return (
    <>
      {Array.from({ length: countLines }, (_, k) => (
        <Placeholder key={k} />
      ))}
    </>
  )
}
